import React, { useState, useEffect } from "react";
import { C, ROLE_PERMISSIONS, SHEIKHS, NATIONALITIES } from "./constants";
import {
  Card, Btn, Field, Modal, ModuleHeader, KPICard, TabBar,
  StatusBadge, DataTable, MeetingsTab, TransactionsTab,
  OperationalPlanTab, ReportsTab, useToast
} from "./erp-shared";
import {
  Globe, Users, UserPlus, BookOpen, Mic, MapPin,
  CheckCircle, Clock, AlertCircle, Plus, Search,
  Eye, Edit, Calendar, Star, Target, Hash,
  Phone, Mail, FileText, Award, TrendingUp,
  Languages, Flag, Heart, Layers
} from "lucide-react";

/* ═════════════════════════════════════════════
   ✦ Communities Page — الجاليات
   ═════════════════════════════════════════════ */
export default function CommunitiesPage({ user }) {
  const [tab, setTab] = useState("overview");
  const [triggers, setTriggers] = useState({ preacher: 0, program: 0, beneficiary: 0, newMuslim: 0 });
  const perms = ROLE_PERMISSIONS[user.role] || {};

  const tabs = [
    { key: "overview", label: "نظرة عامة", icon: Globe },
    { key: "preachers", label: "الدعاة", icon: Mic },
    { key: "programs", label: "البرامج", icon: BookOpen },
    { key: "beneficiaries", label: "المستفيدون", icon: Users },
    { key: "newMuslims", label: "المسلمون الجدد", icon: Heart },
    { key: "meetings", label: "الاجتماعات", icon: Calendar },
    { key: "transactions", label: "المعاملات المالية", icon: FileText },
    { key: "plan", label: "الخطة التشغيلية", icon: Target },
    { key: "reports", label: "التقارير", icon: FileText },
  ];

  function fireQuick(targetTab, triggerKey) {
    setTab(targetTab);
    setTriggers(prev => ({ ...prev, [triggerKey]: prev[triggerKey] + 1 }));
  }

  return (
    <div>
      <ModuleHeader icon={Globe} title="الجاليات" subtitle="إدارة برامج الجاليات والدعاة والمستفيدين" />

      {perms.canEdit && (
        <div style={quickStyles.row}>
          <button style={quickStyles.btn} onClick={() => fireQuick("preachers", "preacher")}>
            <Mic size={16} color="#2563eb" />
            <span>إضافة داعية جديد</span>
          </button>
          <button style={quickStyles.btn} onClick={() => fireQuick("programs", "program")}>
            <BookOpen size={16} color="#0891b2" />
            <span>إضافة برنامج جديد</span>
          </button>
          <button style={quickStyles.btn} onClick={() => fireQuick("beneficiaries", "beneficiary")}>
            <UserPlus size={16} color={C.teal} />
            <span>إضافة مستفيد جديد</span>
          </button>
          <button style={quickStyles.btn} onClick={() => fireQuick("newMuslims", "newMuslim")}>
            <Star size={16} color="#db2777" />
            <span>تسجيل مسلم جديد</span>
          </button>
        </div>
      )}

      <TabBar tabs={tabs} active={tab} onChange={setTab} />

      {tab === "overview" && <OverviewTab />}
      {tab === "preachers" && <PreachersTab canEdit={perms.canEdit} triggerOpen={triggers.preacher} />}
      {tab === "programs" && <ProgramsTab canEdit={perms.canEdit} triggerOpen={triggers.program} />}
      {tab === "beneficiaries" && <BeneficiariesTab canEdit={perms.canEdit} triggerOpen={triggers.beneficiary} />}
      {tab === "newMuslims" && <NewMuslimsTab canEdit={perms.canEdit} triggerOpen={triggers.newMuslim} />}
      {tab === "meetings" && <MeetingsTab departmentName="الجاليات" />}
      {tab === "transactions" && <TransactionsTab budget={850000} />}
      {tab === "plan" && <OperationalPlanTab departmentKey="communities" />}
      {tab === "reports" && <ReportsTab departmentName="الجاليات" />}
    </div>
  );
}

const quickStyles = {
  row: { display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 18 },
  btn: {
    display: "flex", alignItems: "center", gap: 8,
    padding: "10px 18px", borderRadius: 10,
    background: C.white, color: C.primary, border: `1px solid ${C.border}`,
    fontFamily: "'Readex Pro', sans-serif", fontSize: 13, fontWeight: 600,
    cursor: "pointer", transition: "all 0.15s",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  },
};

/* ─────────────────────────────────────────────
   ✦ Overview Tab
   ───────────────────────────────────────────── */
function OverviewTab() {
  return (
    <div style={{ marginTop: 16 }}>
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي المستفيدين" value="5,200" icon={Users} color="#2563eb" trend="+8%" />
        <KPICard title="عدد الدعاة" value="18" icon={Mic} color={C.teal} />
        <KPICard title="البرامج النشطة" value="12" icon={BookOpen} color="#7c3aed" trend="+3" />
        <KPICard title="الجنسيات المخدومة" value="20" icon={Flag} color="#ea580c" />
        <KPICard title="الدروس الأسبوعية" value="42" icon={Calendar} color={C.success} />
        <KPICard title="الميزانية (ريال)" value="850,000" icon={TrendingUp} color={C.accent} />
      </div>

      {/* Top nationalities */}
      <Card style={{ padding: 20, marginBottom: 16 }}>
        <h4 style={styles.sectionTitle}>
          <Flag size={18} color="#ea580c" /> أكبر الجاليات المخدومة
        </h4>
        <div style={styles.natGrid}>
          {TOP_NATIONALITIES.map((n, i) => (
            <div key={i} style={styles.natCard}>
              <div style={{ ...styles.natRank, background: i < 3 ? `${C.accent}20` : C.bg }}>
                {i + 1}
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: C.primary }}>{n.name}</p>
                <p style={{ margin: 0, fontSize: 12, color: C.textLight }}>{n.count} مستفيد</p>
              </div>
              <div style={styles.natBar}>
                <div style={{ width: `${(n.count / 1200) * 100}%`, height: "100%", borderRadius: 4, background: n.color }} />
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Active programs summary */}
      <Card style={{ padding: 20 }}>
        <h4 style={styles.sectionTitle}>
          <BookOpen size={18} color="#7c3aed" /> ملخص البرامج النشطة
        </h4>
        <div style={styles.progSummaryGrid}>
          {PROGRAMS_SUMMARY.map((p, i) => (
            <div key={i} style={styles.progSummaryCard}>
              <div style={{ ...styles.progIcon, background: `${p.color}15` }}>
                <p.icon size={20} color={p.color} />
              </div>
              <div>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: C.primary }}>{p.name}</p>
                <p style={{ margin: "2px 0 0", fontSize: 12, color: C.textLight }}>{p.stat}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Preachers Tab — الدعاة
   ───────────────────────────────────────────── */
const PREACHER_CONTRACT_TYPES = ["متفرغ", "متعاون", "مكافأة"];
const PREACHER_LANGS = ["العربية", "الأردية", "الهندية", "البنغالية", "الفلبينية", "الإنجليزية", "الفرنسية", "أخرى"];
const PREACHER_SPECIALTIES = ["فقه", "عقيدة", "تفسير", "حديث", "تجويد", "سيرة", "دعوة", "لغة عربية"];
const PREACHER_STATUSES = ["نشط", "إجازة", "منتهي"];
const PREACHER_MOSQUES_LIST = ["مسجد التوحيد", "مسجد الإيمان", "مسجد الفرقان", "مسجد السلام", "مسجد الهدى", "مسجد النور", "مسجد الرحمة", "مسجد البر"];
const INITIAL_PREACHER = {
  name: "", nationality: "", nationalId: "", birthDate: "",
  contractType: "متفرغ", salary: "", startDate: "",
  languages: [], specialties: [], mosques: [], lessonsPerWeek: "",
  phone: "", email: "", address: "", education: "",
  status: "نشط", notes: "",
};

function PreachersTab({ canEdit, triggerOpen = 0 }) {
  const [preachers, setPreachers] = useState(DEMO_PREACHERS);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_PREACHER);
  const [showToast, ToastView] = useToast();

  useEffect(() => { if (triggerOpen > 0) setShowAdd(true); /* eslint-disable-next-line */ }, [triggerOpen]);

  const filtered = preachers.filter(p =>
    p.name.includes(search) || p.nationality.includes(search) || p.languages.join(",").includes(search)
  );

  function togglePreacherArr(field, value) {
    setForm(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value],
    }));
  }

  function handleSave() {
    if (!form.name || !form.nationality) {
      showToast("الاسم والجنسية مطلوبان", "error");
      return;
    }
    const newP = {
      id: `p${Date.now()}`,
      name: form.name,
      nationality: form.nationality,
      nationalId: form.nationalId,
      birthDate: form.birthDate,
      contractType: form.contractType,
      type: form.contractType === "متفرغ" ? "fulltime" : "parttime",
      salary: Number(form.salary) || 0,
      startDate: form.startDate,
      languages: [...form.languages],
      specialties: [...form.specialties],
      mosques: [...form.mosques],
      lessonsPerWeek: Number(form.lessonsPerWeek) || 0,
      beneficiaries: 0,
      phone: form.phone,
      email: form.email,
      address: form.address,
      education: form.education,
      status: form.status,
      notes: form.notes,
    };
    setPreachers(prev => [...prev, newP]);
    setForm(INITIAL_PREACHER);
    setShowAdd(false);
    showToast("تمت إضافة الداعية");
  }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي الدعاة" value={preachers.length} icon={Mic} color={C.teal} />
        <KPICard title="متفرغون" value={preachers.filter(p => p.type === "fulltime").length} icon={CheckCircle} color={C.success} />
        <KPICard title="متعاونون" value={preachers.filter(p => p.type === "parttime").length} icon={Clock} color={C.warning} />
        <KPICard title="اللغات" value="8" icon={Languages} color="#7c3aed" />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث بالاسم أو الجنسية أو اللغة..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">إضافة داعية</Btn>}
        </div>
      </Card>

      <div style={styles.preachersGrid}>
        {filtered.map(p => (
          <Card key={p.id} style={styles.preacherCard} onClick={() => setSelected(p)}>
            <div style={styles.preacherTop}>
              <div style={styles.preacherAvatar}>{p.name.charAt(0)}</div>
              <div style={{
                padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600,
                background: p.type === "fulltime" ? `${C.success}15` : `${C.warning}15`,
                color: p.type === "fulltime" ? C.success : C.warning,
              }}>
                {p.type === "fulltime" ? "متفرغ" : "متعاون"}
              </div>
            </div>
            <h4 style={{ margin: "10px 0 2px", fontSize: 15, fontWeight: 700, color: C.primary }}>{p.name}</h4>
            <p style={{ margin: 0, fontSize: 12, color: C.textLight }}>
              <Flag size={12} /> {p.nationality}
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8 }}>
              {p.languages.map((l, i) => (
                <span key={i} style={styles.langTag}>{l}</span>
              ))}
            </div>
            <div style={styles.preacherStats}>
              <span><BookOpen size={12} /> {p.lessonsPerWeek} درس/أسبوع</span>
              <span><Users size={12} /> {p.beneficiaries} مستفيد</span>
            </div>
            <div style={styles.preacherMosques}>
              <MapPin size={12} color={C.textLight} />
              <span style={{ fontSize: 11, color: C.textLight }}>{p.mosques.join("، ")}</span>
            </div>
          </Card>
        ))}
      </div>

      {/* Detail Modal */}
      {selected && (
        <Modal title={`الداعية — ${selected.name}`} onClose={() => setSelected(null)} width={550}>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={styles.detailRow}><strong>الاسم:</strong> {selected.name}</div>
            <div style={styles.detailRow}><strong>الجنسية:</strong> {selected.nationality}</div>
            <div style={styles.detailRow}><strong>النوع:</strong> {selected.type === "fulltime" ? "متفرغ" : "متعاون"}</div>
            <div style={styles.detailRow}><strong>الهاتف:</strong> <span dir="ltr">{selected.phone}</span></div>
            <div style={styles.detailRow}>
              <strong>اللغات:</strong>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
                {selected.languages.map((l, i) => <span key={i} style={styles.langTag}>{l}</span>)}
              </div>
            </div>
            <div style={styles.detailRow}><strong>الدروس الأسبوعية:</strong> {selected.lessonsPerWeek}</div>
            <div style={styles.detailRow}><strong>المستفيدون:</strong> {selected.beneficiaries}</div>
            <div style={styles.detailRow}>
              <strong>المساجد:</strong>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
                {selected.mosques.map((m, i) => <span key={i} style={{ ...styles.langTag, background: `${C.primary}10`, color: C.primary }}>{m}</span>)}
              </div>
            </div>
            {selected.specialties && (
              <div style={styles.detailRow}>
                <strong>التخصصات:</strong>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
                  {selected.specialties.map((s, i) => <span key={i} style={{ ...styles.langTag, background: `${C.accent}15`, color: "#8b6914" }}>{s}</span>)}
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Add Modal */}
      {showAdd && (
        <Modal title="إضافة داعية جديد" onClose={() => { setShowAdd(false); setForm(INITIAL_PREACHER); }} width={760}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Identity */}
            <div>
              <h4 style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700, color: C.teal, borderBottom: `2px solid ${C.teal}30`, paddingBottom: 6 }}>الهوية الشخصية</h4>
              <Field label="الاسم الكامل" required placeholder="الاسم الثلاثي للداعية" value={form.name} onChange={v => setForm({ ...form, name: v })} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                <Field label="الجنسية" type="select" options={NATIONALITIES} required value={form.nationality} onChange={v => setForm({ ...form, nationality: v })} />
                <Field label="رقم الهوية/الإقامة" placeholder="10xxxxxxxx" value={form.nationalId} onChange={v => setForm({ ...form, nationalId: v })} />
                <Field label="تاريخ الميلاد" type="date" value={form.birthDate} onChange={v => setForm({ ...form, birthDate: v })} />
              </div>
            </div>

            {/* Contract */}
            <div>
              <h4 style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700, color: C.teal, borderBottom: `2px solid ${C.teal}30`, paddingBottom: 6 }}>التعاقد</h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                <Field label="نوع التعاقد" type="select" options={PREACHER_CONTRACT_TYPES} value={form.contractType} onChange={v => setForm({ ...form, contractType: v })} />
                <Field label="الراتب/المكافأة الشهرية (ر.س)" type="number" placeholder="0" value={form.salary} onChange={v => setForm({ ...form, salary: v })} />
                <Field label="تاريخ بداية العمل" type="date" value={form.startDate} onChange={v => setForm({ ...form, startDate: v })} />
              </div>
            </div>

            {/* Languages */}
            <div>
              <label style={{ display: "block", marginBottom: 6, fontSize: 13, fontWeight: 600, color: C.textLight }}>
                اللغات ({form.languages.length} مختار)
              </label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 6, padding: 10, background: C.bg, borderRadius: 8 }}>
                {PREACHER_LANGS.map(l => (
                  <label key={l} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer" }}>
                    <input type="checkbox" checked={form.languages.includes(l)} onChange={() => togglePreacherArr("languages", l)} />
                    <span>{l}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Specialties */}
            <div>
              <label style={{ display: "block", marginBottom: 6, fontSize: 13, fontWeight: 600, color: C.textLight }}>
                التخصصات ({form.specialties.length} مختار)
              </label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 6, padding: 10, background: C.bg, borderRadius: 8 }}>
                {PREACHER_SPECIALTIES.map(s => (
                  <label key={s} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer" }}>
                    <input type="checkbox" checked={form.specialties.includes(s)} onChange={() => togglePreacherArr("specialties", s)} />
                    <span>{s}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Mosques */}
            <div>
              <label style={{ display: "block", marginBottom: 6, fontSize: 13, fontWeight: 600, color: C.textLight }}>
                المساجد المكلّف بها ({form.mosques.length} مختار)
              </label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, padding: 10, background: C.bg, borderRadius: 8 }}>
                {PREACHER_MOSQUES_LIST.map(m => (
                  <label key={m} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer" }}>
                    <input type="checkbox" checked={form.mosques.includes(m)} onChange={() => togglePreacherArr("mosques", m)} />
                    <span>{m}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Contact + Education */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label="عدد الدروس الأسبوعية" type="number" placeholder="0" value={form.lessonsPerWeek} onChange={v => setForm({ ...form, lessonsPerWeek: v })} />
              <Field label="الهاتف" placeholder="05xxxxxxxx" value={form.phone} onChange={v => setForm({ ...form, phone: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label="البريد الإلكتروني" placeholder="email@example.com" value={form.email} onChange={v => setForm({ ...form, email: v })} />
              <Field label="العنوان" placeholder="المدينة - الحي" value={form.address} onChange={v => setForm({ ...form, address: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label="المؤهل العلمي" placeholder="مثال: بكالوريوس شريعة" value={form.education} onChange={v => setForm({ ...form, education: v })} />
              <Field label="الحالة" type="select" options={PREACHER_STATUSES} value={form.status} onChange={v => setForm({ ...form, status: v })} />
            </div>
            <Field label="ملاحظات" type="textarea" placeholder="ملاحظات إضافية..." value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Mic} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_PREACHER); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Programs Tab — البرامج
   ───────────────────────────────────────────── */
const INITIAL_PROGRAM = {
  name: "", type: "", targetGroup: "", language: "العربية",
  owner: "", location: "", schedule: "",
  beneficiaries: "", budget: "",
  startDate: "", endDate: "",
  goals: "", description: "",
  statusLabel: "مخطط",
};
const PROGRAM_TYPES = ["دروس أسبوعية","دورة مكثفة","محاضرات","مسابقات","رحلات دعوية","إفطار جماعي","حلقات تحفيظ"];
const PROGRAM_STATUS_OPTIONS = ["نشط","مخطط","منتهي","معلّق"];
const PROGRAM_STATUS_LABEL_TO_KEY = { "نشط": "active", "مخطط": "planned", "منتهي": "completed", "معلّق": "paused" };
const PROGRAM_LOCATIONS = ["مسجد التوحيد","مسجد الإيمان","مسجد الفرقان","مسجد السلام","مسجد الهدى","مسجد النور","مسجد الرحمة","قاعة الجمعية","قاعة الملك فهد"];

function ProgramsTab({ canEdit, triggerOpen = 0 }) {
  const [programs, setPrograms] = useState(DEMO_PROGRAMS);
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(INITIAL_PROGRAM);
  const [showToast, ToastView] = useToast();

  useEffect(() => { if (triggerOpen > 0) setShowAdd(true); /* eslint-disable-next-line */ }, [triggerOpen]);

  const filtered = programs.filter(p => p.name.includes(search) || p.type.includes(search));

  const PROG_STATUSES = [
    { key: "active", label: "نشط", color: C.success },
    { key: "planned", label: "مخطط", color: C.warning },
    { key: "completed", label: "منتهي", color: C.textLight },
    { key: "paused", label: "معلّق", color: C.danger },
  ];

  function handleSave() {
    if (!form.name || !form.type) {
      showToast("الاسم والنوع مطلوبان", "error");
      return;
    }
    const newP = {
      id: `pr${Date.now()}`,
      name: form.name,
      type: form.type,
      targetGroup: form.targetGroup || "الجميع",
      language: form.language,
      owner: form.owner || "—",
      location: form.location || "—",
      schedule: form.schedule || "",
      beneficiaries: Number(form.beneficiaries) || 0,
      budget: Number(form.budget) || 0,
      startDate: form.startDate || "",
      endDate: form.endDate || "",
      goals: form.goals || "",
      description: form.description,
      status: PROGRAM_STATUS_LABEL_TO_KEY[form.statusLabel] || "planned",
    };
    setPrograms(prev => [...prev, newP]);
    setForm(INITIAL_PROGRAM);
    setShowAdd(false);
    showToast("تمت إضافة البرنامج");
  }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي البرامج" value={programs.length} icon={BookOpen} color="#7c3aed" />
        <KPICard title="برامج نشطة" value={programs.filter(p => p.status === "active").length} icon={CheckCircle} color={C.success} />
        <KPICard title="المستفيدون" value="5,200" icon={Users} color="#2563eb" />
        <KPICard title="الميزانية" value="520K" icon={TrendingUp} color={C.accent} />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">برنامج جديد</Btn>}
        </div>
      </Card>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "name", label: "اسم البرنامج", render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
            { key: "type", label: "النوع", width: "120px" },
            { key: "targetGroup", label: "الفئة المستهدفة", width: "130px" },
            { key: "beneficiaries", label: "المستفيدون", width: "100px", render: v => v.toLocaleString() },
            { key: "budget", label: "الميزانية", width: "110px", render: v => `${v.toLocaleString()} ر` },
            { key: "status", label: "الحالة", width: "100px", render: v => <StatusBadge status={v} statuses={PROG_STATUSES} /> },
          ]}
          data={filtered}
          onRowClick={row => setSelected(row)}
        />
      </Card>

      {selected && (
        <Modal title={`برنامج — ${selected.name}`} onClose={() => setSelected(null)} width={600}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={styles.detailRow}><strong>الاسم:</strong> {selected.name}</div>
            <div style={styles.detailRow}><strong>النوع:</strong> {selected.type}</div>
            <div style={styles.detailRow}><strong>الفئة المستهدفة:</strong> {selected.targetGroup}</div>
            {selected.language && <div style={styles.detailRow}><strong>اللغة:</strong> {selected.language}</div>}
            {selected.owner && <div style={styles.detailRow}><strong>الداعية/المعلم المسؤول:</strong> {selected.owner}</div>}
            {selected.location && <div style={styles.detailRow}><strong>المكان:</strong> {selected.location}</div>}
            {selected.schedule && <div style={styles.detailRow}><strong>الموعد والجدول:</strong> {selected.schedule}</div>}
            <div style={styles.detailRow}><strong>المستفيدون:</strong> {selected.beneficiaries.toLocaleString()}</div>
            <div style={styles.detailRow}><strong>الميزانية:</strong> {selected.budget.toLocaleString()} ريال</div>
            {(selected.startDate || selected.endDate) && (
              <div style={styles.detailRow}><strong>الفترة:</strong> {selected.startDate || "—"} → {selected.endDate || "—"}</div>
            )}
            {selected.goals && (
              <div style={styles.detailRow}>
                <strong>الأهداف:</strong>
                <p style={{ margin: "4px 0 0", fontSize: 13, color: C.primary, lineHeight: 1.7 }}>{selected.goals}</p>
              </div>
            )}
            <div style={styles.detailRow}>
              <strong>الوصف التفصيلي:</strong>
              <p style={{ margin: "4px 0 0", fontSize: 13, color: C.primary, lineHeight: 1.7 }}>{selected.description}</p>
            </div>
          </div>
        </Modal>
      )}

      {showAdd && (
        <Modal title="إضافة برنامج جديد" onClose={() => { setShowAdd(false); setForm(INITIAL_PROGRAM); }} width={720}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="اسم البرنامج" required placeholder="مثال: دروس اللغة العربية" value={form.name} onChange={v => setForm({ ...form, name: v })} />

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="النوع" type="select" options={PROGRAM_TYPES} required value={form.type} onChange={v => setForm({ ...form, type: v })} />
              <Field label="الفئة المستهدفة" type="select" options={NATIONALITIES.concat(["الجميع"])} value={form.targetGroup} onChange={v => setForm({ ...form, targetGroup: v })} />
              <Field label="اللغة الرئيسية" type="select" options={LANGUAGES} value={form.language} onChange={v => setForm({ ...form, language: v })} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="الداعية / المعلم المسؤول" type="select" options={DEMO_PREACHERS.map(p => p.name)} value={form.owner} onChange={v => setForm({ ...form, owner: v })} />
              <Field label="المسجد / المكان" type="select" options={PROGRAM_LOCATIONS} value={form.location} onChange={v => setForm({ ...form, location: v })} />
            </div>

            <Field label="الموعد والجدول" placeholder="مثال: الأحد والثلاثاء 4-6م" value={form.schedule} onChange={v => setForm({ ...form, schedule: v })} />

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="عدد المستفيدين المتوقع" type="number" placeholder="0" value={form.beneficiaries} onChange={v => setForm({ ...form, beneficiaries: v })} />
              <Field label="الميزانية المقدرة (ريال)" type="number" placeholder="0" value={form.budget} onChange={v => setForm({ ...form, budget: v })} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="تاريخ البداية" type="date" value={form.startDate} onChange={v => setForm({ ...form, startDate: v })} />
              <Field label="تاريخ النهاية" type="date" value={form.endDate} onChange={v => setForm({ ...form, endDate: v })} />
              <Field label="الحالة" type="select" options={PROGRAM_STATUS_OPTIONS} value={form.statusLabel} onChange={v => setForm({ ...form, statusLabel: v })} />
            </div>

            <Field label="أهداف البرنامج" type="textarea" placeholder="الأهداف الاستراتيجية للبرنامج..." rows={3} value={form.goals} onChange={v => setForm({ ...form, goals: v })} />
            <Field label="وصف تفصيلي" type="textarea" placeholder="وصف شامل للبرنامج..." rows={3} value={form.description} onChange={v => setForm({ ...form, description: v })} />

            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={BookOpen} onClick={handleSave}>حفظ البرنامج</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_PROGRAM); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Beneficiaries Tab — المستفيدون
   ───────────────────────────────────────────── */
const INITIAL_BENEFICIARY = { name: "", nationality: "", gender: "", age: "", program: "", mosque: "", phone: "", registrationDate: "" };

function BeneficiariesTab({ canEdit, triggerOpen = 0 }) {
  const [beneficiaries, setBeneficiaries] = useState(DEMO_BENEFICIARIES);
  const [search, setSearch] = useState("");
  const [filterNat, setFilterNat] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_BENEFICIARY);
  const [showToast, ToastView] = useToast();

  useEffect(() => { if (triggerOpen > 0) setShowAdd(true); /* eslint-disable-next-line */ }, [triggerOpen]);

  const filtered = beneficiaries.filter(b => {
    const matchSearch = b.name.includes(search) || b.nationality.includes(search);
    const matchNat = filterNat === "all" || b.nationality === filterNat;
    return matchSearch && matchNat;
  });

  const nationalities = [...new Set(beneficiaries.map(b => b.nationality))];

  function handleSave() {
    if (!form.name || !form.nationality || !form.gender) {
      showToast("الاسم والجنسية والجنس مطلوبة", "error");
      return;
    }
    const newB = {
      id: `b${Date.now()}`,
      name: form.name,
      nationality: form.nationality,
      gender: form.gender,
      age: Number(form.age) || 0,
      program: form.program || "—",
      mosque: form.mosque || "—",
      joinDate: new Date().toISOString().slice(0, 10),
      phone: form.phone,
    };
    setBeneficiaries(prev => [...prev, newB]);
    setForm(INITIAL_BENEFICIARY);
    setShowAdd(false);
    showToast("تمت إضافة المستفيد");
  }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي المستفيدين" value={beneficiaries.length} icon={Users} color="#2563eb" />
        <KPICard title="ذكور" value={beneficiaries.filter(b => b.gender === "ذكر").length} icon={Users} color={C.primary} />
        <KPICard title="إناث" value={beneficiaries.filter(b => b.gender === "أنثى").length} icon={Heart} color="#db2777" />
        <KPICard title="جنسيات" value={nationalities.length} icon={Flag} color="#ea580c" />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث بالاسم أو الجنسية..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select value={filterNat} onChange={e => setFilterNat(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع الجنسيات</option>
              {nationalities.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">إضافة مستفيد</Btn>}
          </div>
        </div>
      </Card>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "name", label: "الاسم", render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
            { key: "nationality", label: "الجنسية", width: "110px" },
            { key: "gender", label: "الجنس", width: "80px" },
            { key: "age", label: "العمر", width: "70px" },
            { key: "program", label: "البرنامج", width: "140px" },
            { key: "mosque", label: "المسجد", width: "130px" },
            { key: "joinDate", label: "تاريخ التسجيل", width: "110px" },
          ]}
          data={filtered}
        />
      </Card>

      {showAdd && (
        <Modal title="إضافة مستفيد" onClose={() => { setShowAdd(false); setForm(INITIAL_BENEFICIARY); }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="الاسم الكامل" required placeholder="اسم المستفيد" value={form.name} onChange={v => setForm({ ...form, name: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="الجنسية" type="select" options={NATIONALITIES} required value={form.nationality} onChange={v => setForm({ ...form, nationality: v })} />
              <Field label="الجنس" type="select" options={["ذكر","أنثى"]} required value={form.gender} onChange={v => setForm({ ...form, gender: v })} />
              <Field label="العمر" type="number" placeholder="0" value={form.age} onChange={v => setForm({ ...form, age: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="البرنامج" type="select" options={["دروس عربية","تلاوة قرآن","فقه العبادات","محادثة عربية","دورة العقيدة"]} value={form.program} onChange={v => setForm({ ...form, program: v })} />
              <Field label="المسجد" type="select" options={["مسجد التوحيد","مسجد الإيمان","مسجد الفرقان","مسجد السلام","مسجد الهدى"]} value={form.mosque} onChange={v => setForm({ ...form, mosque: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="الهاتف" placeholder="05XXXXXXXX" value={form.phone} onChange={v => setForm({ ...form, phone: v })} />
              <Field label="تاريخ التسجيل" type="date" value={form.registrationDate} onChange={v => setForm({ ...form, registrationDate: v })} />
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={UserPlus} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_BENEFICIARY); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ New Muslims Tab — المسلمون الجدد
   ───────────────────────────────────────────── */
const NM_NATIONALITIES = ["هندي", "بنغالي", "فلبيني", "إثيوبي", "نيبالي", "باكستاني", "بريطاني", "أمريكي", "صيني", "أخرى"];
const LANGUAGES = ["هندية", "بنغالية", "فلبينية", "أمهرية", "نيبالية", "أوردو", "إنجليزية", "صينية"];
const NM_PREACHERS = [
  "الشيخ يوسف الحربي",
  "الشيخ أحمد العتيبي",
  "الشيخ خالد القحطاني",
  "الشيخ عمر السبيعي",
  "الشيخ سعد الزهراني",
  "الشيخ فيصل الدوسري",
  "الشيخ ناصر الشمري",
];
const NM_PROGRAMS = ["علمني الفاتحة", "كفالة مسلم جديد", "ملتقيات بشائر الخير", "أساسيات الإسلام"];
const FOLLOWUP_STATUSES = [
  { key: "active",     label: "متابعة نشطة",       color: C.success },
  { key: "in_program", label: "في برنامج تعليمي",  color: C.teal },
  { key: "completed",  label: "أكمل البرنامج",     color: C.accent },
  { key: "stopped",    label: "انقطع",              color: C.danger },
];
const EDU_LEVELS = ["مبتدئ", "متوسط", "متقدم"];
const CERT_STATUSES = ["لم تُصدر", "أُصدرت", "استلم", "لم يستلم"];

const INITIAL_NEW_MUSLIM = {
  nameEn: "", nameAr: "", nameAfterIslam: "",
  nationality: "", language: "", islamDate: "",
  phone: "", preacher: "", program: "", followupLabel: "متابعة نشطة",
  eduLevel: "مبتدئ", certStatus: "لم تُصدر",
  learnedBasics: false, managerApproved: false,
};

function NewMuslimsTab({ canEdit, triggerOpen = 0 }) {
  const [data, setData] = useState(DEMO_NEW_MUSLIMS);
  const [search, setSearch] = useState("");
  const [filterNat, setFilterNat] = useState("all");
  const [filterFollow, setFilterFollow] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(INITIAL_NEW_MUSLIM);
  const [showToast, ToastView] = useToast();

  useEffect(() => { if (triggerOpen > 0) setShowAdd(true); /* eslint-disable-next-line */ }, [triggerOpen]);

  const followLabelToKey = Object.fromEntries(FOLLOWUP_STATUSES.map(s => [s.label, s.key]));
  const nationalities = [...new Set(data.map(n => n.nationality))];

  const filtered = data.filter(n => {
    const ms = !search || n.nameEn.includes(search) || (n.nameAr && n.nameAr.includes(search)) || (n.nameAfterIslam && n.nameAfterIslam.includes(search));
    const mn = filterNat === "all" || n.nationality === filterNat;
    const mf = filterFollow === "all" || n.followup === filterFollow;
    return ms && mn && mf;
  });

  const thisMonth = new Date().toISOString().slice(0, 7);
  const monthCount = data.filter(n => (n.islamDate || "").startsWith(thisMonth)).length;
  const activeCount = data.filter(n => n.followup === "active").length;
  const completedCount = data.filter(n => n.followup === "completed").length;

  function handleSave() {
    if (!form.nameEn || !form.nationality || !form.islamDate) {
      showToast("الاسم الإنجليزي والجنسية وتاريخ الإسلام مطلوبة", "error");
      return;
    }
    const newRec = {
      id: `nm${Date.now()}`,
      nameEn: form.nameEn,
      nameAr: form.nameAr,
      nameAfterIslam: form.nameAfterIslam,
      nationality: form.nationality,
      language: form.language,
      islamDate: form.islamDate,
      phone: form.phone,
      preacher: form.preacher || "—",
      program: form.program || "—",
      followup: followLabelToKey[form.followupLabel] || "active",
      eduLevel: form.eduLevel,
      certStatus: form.certStatus,
      learnedBasics: form.learnedBasics,
      managerApproved: form.managerApproved,
    };
    setData(prev => [newRec, ...prev]);
    setForm(INITIAL_NEW_MUSLIM);
    setShowAdd(false);
    showToast("تم تسجيل المسلم الجديد");
  }

  const certIssued = data.filter(n => n.certStatus === "أُصدرت" || n.certStatus === "استلم").length;

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}

      <div style={styles.kpiRow}>
        <KPICard title="إجمالي المسلمين الجدد" value={data.length} icon={Heart} color="#db2777" />
        <KPICard title="هذا الشهر" value={monthCount} icon={Calendar} color={C.teal} />
        <KPICard title="متابعة نشطة" value={activeCount} icon={CheckCircle} color={C.success} />
        <KPICard title="أكملوا البرنامج" value={completedCount} icon={Star} color={C.accent} />
        <KPICard title="شهادات صدرت" value={certIssued} icon={FileText} color="#7c3aed" />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input
              type="text"
              placeholder="بحث بالاسم..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={styles.searchInput}
            />
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <select value={filterNat} onChange={e => setFilterNat(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع الجنسيات</option>
              {nationalities.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <select value={filterFollow} onChange={e => setFilterFollow(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع حالات المتابعة</option>
              {FOLLOWUP_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">تسجيل مسلم جديد</Btn>}
          </div>
        </div>
      </Card>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "nameEn", label: "الاسم (En)", render: v => <span style={{ fontWeight: 600 }} dir="ltr">{v}</span> },
            { key: "nameAr", label: "الاسم بالعربية", render: v => v || "—" },
            { key: "nationality", label: "الجنسية", width: "100px" },
            { key: "islamDate", label: "تاريخ الإسلام", width: "110px" },
            { key: "preacher", label: "الداعية", width: "150px" },
            { key: "program", label: "البرنامج", width: "150px" },
            { key: "followup", label: "حالة المتابعة", width: "130px", render: v => <StatusBadge status={v} statuses={FOLLOWUP_STATUSES} /> },
            { key: "certStatus", label: "حالة الشهادة", width: "110px" },
          ]}
          data={filtered}
          onRowClick={row => setSelected(row)}
        />
      </Card>

      {/* Detail */}
      {selected && (
        <Modal title={`مسلم جديد — ${selected.nameAfterIslam || selected.nameEn}`} onClose={() => setSelected(null)} width={580}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={styles.detailRow}><strong>الاسم الإنجليزي:</strong> <span dir="ltr">{selected.nameEn}</span></div>
            {selected.nameAr && <div style={styles.detailRow}><strong>الاسم بالعربية:</strong> {selected.nameAr}</div>}
            {selected.nameAfterIslam && <div style={styles.detailRow}><strong>الاسم بعد الإسلام:</strong> {selected.nameAfterIslam}</div>}
            <div style={styles.detailRow}><strong>الجنسية:</strong> {selected.nationality}</div>
            <div style={styles.detailRow}><strong>اللغة:</strong> {selected.language || "—"}</div>
            <div style={styles.detailRow}><strong>تاريخ الإسلام:</strong> {selected.islamDate}</div>
            <div style={styles.detailRow}><strong>الجوال:</strong> <span dir="ltr">{selected.phone || "—"}</span></div>
            <div style={styles.detailRow}><strong>الداعية المسؤول:</strong> {selected.preacher}</div>
            <div style={styles.detailRow}><strong>البرنامج:</strong> {selected.program}</div>
            <div style={styles.detailRow}><strong>حالة المتابعة:</strong> <StatusBadge status={selected.followup} statuses={FOLLOWUP_STATUSES} /></div>
            <div style={styles.detailRow}><strong>المستوى التعليمي:</strong> {selected.eduLevel}</div>
            <div style={styles.detailRow}><strong>شهادة الإسلام:</strong> {selected.certStatus}</div>
            <div style={styles.detailRow}><strong>أتمّ تعلّم أساسيات الإسلام:</strong> {selected.learnedBasics ? "✓ نعم" : "لا"}</div>
            <div style={styles.detailRow}><strong>اعتماد مدير الإدارة:</strong> {selected.managerApproved ? "✓ معتمد" : "بانتظار الاعتماد"}</div>
          </div>
        </Modal>
      )}

      {/* Add Modal */}
      {showAdd && (
        <Modal title="تسجيل مسلم جديد" onClose={() => { setShowAdd(false); setForm(INITIAL_NEW_MUSLIM); }} width={760}>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {/* Top banner */}
            <div style={{
              padding: "14px 18px", borderRadius: 12,
              background: `linear-gradient(135deg, ${C.teal}12, ${C.teal}06)`,
              border: `1px solid ${C.teal}30`,
              display: "flex", alignItems: "center", gap: 12,
            }}>
              <span style={{ fontSize: 22 }}>✨</span>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: C.primary, lineHeight: 1.6 }}>
                سجّل بيانات المسلم الجديد بدقة لمتابعته في برامج التعليم والكفالة
              </p>
            </div>

            {/* Section 1 — Basic Data */}
            <div>
              <h4 style={{ margin: "0 0 12px", fontSize: 15, color: C.teal, borderBottom: `2px solid ${C.teal}30`, paddingBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 18 }}>👥</span> البيانات الأساسية
              </h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                <Field label="الاسم بالإنجليزية" required placeholder="Mohamed Ahmed" value={form.nameEn} onChange={v => setForm({ ...form, nameEn: v })} />
                <Field label="الاسم بالعربية" placeholder="محمد أحمد" value={form.nameAr} onChange={v => setForm({ ...form, nameAr: v })} />
                <Field label="الاسم بعد الإسلام (اختياري)" placeholder="عبدالله" value={form.nameAfterIslam} onChange={v => setForm({ ...form, nameAfterIslam: v })} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                <Field label="الجنسية" type="select" options={NM_NATIONALITIES} required value={form.nationality} onChange={v => setForm({ ...form, nationality: v })} />
                <Field label="اللغة" type="select" options={LANGUAGES} value={form.language} onChange={v => setForm({ ...form, language: v })} />
                <Field label="تاريخ الإسلام" type="date" required value={form.islamDate} onChange={v => setForm({ ...form, islamDate: v })} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                <Field label="الجوال" placeholder="05xxxxxxxx" value={form.phone} onChange={v => setForm({ ...form, phone: v })} />
                <Field label="الداعية المسؤول" type="select" options={NM_PREACHERS} value={form.preacher} onChange={v => setForm({ ...form, preacher: v })} />
                <Field label="البرنامج" type="select" options={NM_PROGRAMS} value={form.program} onChange={v => setForm({ ...form, program: v })} />
              </div>
              <Field label="حالة المتابعة" type="select" options={FOLLOWUP_STATUSES.map(s => s.label)} value={form.followupLabel} onChange={v => setForm({ ...form, followupLabel: v })} />
            </div>

            {/* Section 2 — Education & Documents */}
            <div>
              <h4 style={{ margin: "0 0 12px", fontSize: 15, color: C.teal, borderBottom: `2px solid ${C.teal}30`, paddingBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 18 }}>📝</span> المستوى التعليمي والوثائق
              </h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Field label="المستوى التعليمي" type="select" options={EDU_LEVELS} value={form.eduLevel} onChange={v => setForm({ ...form, eduLevel: v })} />
                <Field label="حالة الوثيقة (شهادة الإسلام)" type="select" options={CERT_STATUSES} value={form.certStatus} onChange={v => setForm({ ...form, certStatus: v })} />
              </div>
            </div>

            {/* Section 3 — Verification & Approval */}
            <div>
              <h4 style={{ margin: "0 0 12px", fontSize: 15, color: C.teal, borderBottom: `2px solid ${C.teal}30`, paddingBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 18 }}>✅</span> التحقق والاعتماد
              </h4>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <label style={{ display: "flex", flexDirection: "column", padding: 14, background: C.bg, borderRadius: 10, border: `1px solid ${form.learnedBasics ? C.success : C.border}`, cursor: "pointer", gap: 4 }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <input type="checkbox" checked={form.learnedBasics} onChange={e => setForm({ ...form, learnedBasics: e.target.checked })} />
                    <span style={{ fontSize: 14, fontWeight: 600, color: C.primary }}>أتمّ تعلّم أساسيات الإسلام</span>
                  </span>
                  <span style={{ fontSize: 12, color: C.textLight, marginRight: 26 }}>الشهادتين، الصلاة، الوضوء، أركان الإيمان</span>
                </label>
                <label style={{ display: "flex", flexDirection: "column", padding: 14, background: C.bg, borderRadius: 10, border: `1px solid ${form.managerApproved ? C.success : C.border}`, cursor: "pointer", gap: 4 }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <input type="checkbox" checked={form.managerApproved} onChange={e => setForm({ ...form, managerApproved: e.target.checked })} />
                    <span style={{ fontSize: 14, fontWeight: 600, color: C.primary }}>اعتماد مدير الإدارة</span>
                  </span>
                  <span style={{ fontSize: 12, color: C.textLight, marginRight: 26 }}>يتم بعد مراجعة البيانات والتأكد من اكتمالها</span>
                </label>
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Heart} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_NEW_MUSLIM); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

const DEMO_NEW_MUSLIMS = [
  { id: "nm1", nameEn: "John Smith", nameAr: "جون سميث", nameAfterIslam: "عبدالله", nationality: "بريطاني", language: "إنجليزية", islamDate: "2026-01-15", phone: "0551234001", preacher: "الشيخ يوسف الحربي", program: "أساسيات الإسلام", followup: "active", eduLevel: "مبتدئ", certStatus: "أُصدرت", learnedBasics: true, managerApproved: true },
  { id: "nm2", nameEn: "Ramesh Kumar", nameAr: "راميش كومار", nameAfterIslam: "محمد كريم", nationality: "هندي", language: "هندية", islamDate: "2026-02-20", phone: "0551234002", preacher: "الشيخ أحمد العتيبي", program: "علمني الفاتحة", followup: "in_program", eduLevel: "متوسط", certStatus: "استلم", learnedBasics: true, managerApproved: true },
  { id: "nm3", nameEn: "Rahima Begum", nameAr: "رحيمة بيغوم", nameAfterIslam: "آمنة", nationality: "بنغالي", language: "بنغالية", islamDate: "2026-03-05", phone: "0551234003", preacher: "الشيخ خالد القحطاني", program: "كفالة مسلم جديد", followup: "active", eduLevel: "مبتدئ", certStatus: "أُصدرت", learnedBasics: true, managerApproved: false },
  { id: "nm4", nameEn: "Sunita Sharma", nameAr: "سونيتا شارما", nameAfterIslam: "خديجة", nationality: "نيبالي", language: "نيبالية", islamDate: "2025-11-10", phone: "0551234004", preacher: "الشيخ عمر السبيعي", program: "أساسيات الإسلام", followup: "completed", eduLevel: "متقدم", certStatus: "استلم", learnedBasics: true, managerApproved: true },
  { id: "nm5", nameEn: "Anil Tharu", nameAr: "أنيل ثارو", nameAfterIslam: "", nationality: "نيبالي", language: "نيبالية", islamDate: "2026-04-02", phone: "0551234005", preacher: "الشيخ سعد الزهراني", program: "علمني الفاتحة", followup: "active", eduLevel: "مبتدئ", certStatus: "لم تُصدر", learnedBasics: false, managerApproved: false },
  { id: "nm6", nameEn: "Maria Santos", nameAr: "ماريا سانتوس", nameAfterIslam: "مريم", nationality: "فلبيني", language: "فلبينية", islamDate: "2026-04-18", phone: "0551234006", preacher: "الشيخ فيصل الدوسري", program: "ملتقيات بشائر الخير", followup: "in_program", eduLevel: "مبتدئ", certStatus: "أُصدرت", learnedBasics: true, managerApproved: true },
  { id: "nm7", nameEn: "Joseph Mwangi", nameAr: "جوزيف موانجي", nameAfterIslam: "يوسف", nationality: "إثيوبي", language: "أمهرية", islamDate: "2025-09-12", phone: "0551234007", preacher: "الشيخ ناصر الشمري", program: "كفالة مسلم جديد", followup: "stopped", eduLevel: "متوسط", certStatus: "استلم", learnedBasics: true, managerApproved: true },
  { id: "nm8", nameEn: "Wei Zhang", nameAr: "وي تشانغ", nameAfterIslam: "إبراهيم", nationality: "صيني", language: "صينية", islamDate: "2026-05-05", phone: "0551234008", preacher: "الشيخ يوسف الحربي", program: "أساسيات الإسلام", followup: "in_program", eduLevel: "مبتدئ", certStatus: "لم يستلم", learnedBasics: false, managerApproved: false },
  { id: "nm9", nameEn: "Mohammed Tariq", nameAr: "محمد طارق", nameAfterIslam: "—", nationality: "باكستاني", language: "أوردو", islamDate: "2026-05-12", phone: "0551234009", preacher: "الشيخ أحمد العتيبي", program: "علمني الفاتحة", followup: "active", eduLevel: "مبتدئ", certStatus: "أُصدرت", learnedBasics: true, managerApproved: true },
  { id: "nm10", nameEn: "Anwar Hossain", nameAr: "أنور حسين", nameAfterIslam: "—", nationality: "بنغالي", language: "بنغالية", islamDate: "2025-08-20", phone: "0551234010", preacher: "الشيخ خالد القحطاني", program: "أساسيات الإسلام", followup: "completed", eduLevel: "متقدم", certStatus: "استلم", learnedBasics: true, managerApproved: true },
];

/* ═════════════════════════════════════════════
   ✦ Demo Data
   ═════════════════════════════════════════════ */
const TOP_NATIONALITIES = [
  { name: "هندية", count: 1200, color: "#ea580c" },
  { name: "باكستانية", count: 980, color: "#16a34a" },
  { name: "بنغالية", count: 750, color: "#2563eb" },
  { name: "فلبينية", count: 620, color: "#7c3aed" },
  { name: "إندونيسية", count: 480, color: "#0891b2" },
  { name: "أفريقية", count: 420, color: "#db2777" },
  { name: "نيبالية", count: 350, color: "#d97706" },
  { name: "أخرى", count: 400, color: "#6366f1" },
];

const PROGRAMS_SUMMARY = [
  { name: "دروس اللغة العربية", stat: "18 حلقة • 1,200 مستفيد", icon: BookOpen, color: "#2563eb" },
  { name: "حلقات تحفيظ القرآن", stat: "12 حلقة • 850 مستفيد", icon: Star, color: C.teal },
  { name: "دورات الفقه والعقيدة", stat: "8 دورات • 620 مستفيد", icon: Award, color: "#7c3aed" },
  { name: "الرحلات الدعوية", stat: "4 رحلات • 340 مستفيد", icon: MapPin, color: "#ea580c" },
  { name: "إفطار الصائمين", stat: "45 يوم • 2,100 مستفيد", icon: Heart, color: "#db2777" },
  { name: "المسابقات القرآنية", stat: "3 مسابقات • 180 مستفيد", icon: Target, color: C.accent },
];

const DEMO_PREACHERS = [
  { id: "p1", name: "الشيخ عبدالرحمن السبيعي", nationality: "سعودي", type: "fulltime", phone: "0551234567", languages: ["العربية","الأردية","الإنجليزية"], lessonsPerWeek: 6, beneficiaries: 420, mosques: ["مسجد التوحيد","مسجد الإيمان"], specialties: ["فقه","عقيدة","تلاوة"] },
  { id: "p2", name: "الشيخ محمد الراشدي", nationality: "سعودي", type: "fulltime", phone: "0559876543", languages: ["العربية","الهندية"], lessonsPerWeek: 5, beneficiaries: 380, mosques: ["مسجد الفرقان","مسجد الهدى"], specialties: ["تفسير","حديث"] },
  { id: "p3", name: "الشيخ إبراهيم خان", nationality: "باكستاني", type: "fulltime", phone: "0553456789", languages: ["الأردية","العربية","البشتو"], lessonsPerWeek: 4, beneficiaries: 350, mosques: ["مسجد السلام"], specialties: ["قرآن","فقه حنفي"] },
  { id: "p4", name: "الشيخ عمر ديالو", nationality: "سنغالي", type: "parttime", phone: "0557891234", languages: ["الفرنسية","العربية","الولوف"], lessonsPerWeek: 3, beneficiaries: 220, mosques: ["مسجد الرحمة"], specialties: ["دعوة","لغة عربية"] },
  { id: "p5", name: "الشيخ أحمد سوبرامان", nationality: "هندي", type: "fulltime", phone: "0552345678", languages: ["الهندية","التاميل","العربية","الإنجليزية"], lessonsPerWeek: 5, beneficiaries: 410, mosques: ["مسجد التوحيد","مسجد النور"], specialties: ["تجويد","عقيدة"] },
  { id: "p6", name: "الشيخ عبدالله بانغورا", nationality: "غيني", type: "parttime", phone: "0558765432", languages: ["الفرنسية","العربية","المالنكي"], lessonsPerWeek: 2, beneficiaries: 150, mosques: ["مسجد الإيمان"], specialties: ["دعوة","سيرة"] },
  { id: "p7", name: "الشيخ يوسف آل ثاني", nationality: "سعودي", type: "fulltime", phone: "0554567890", languages: ["العربية","الإنجليزية"], lessonsPerWeek: 4, beneficiaries: 280, mosques: ["مسجد الهدى","مسجد الرحمة"], specialties: ["فقه","أصول"] },
  { id: "p8", name: "الشيخ رحمة الله بنغالي", nationality: "بنغالي", type: "fulltime", phone: "0556789012", languages: ["البنغالية","العربية","الهندية"], lessonsPerWeek: 4, beneficiaries: 320, mosques: ["مسجد السلام","مسجد النور"], specialties: ["قرآن","تجويد","لغة عربية"] },
];

const DEMO_PROGRAMS = [
  { id: "pr1", name: "دروس اللغة العربية للجاليات", type: "دروس أسبوعية", targetGroup: "الجميع", beneficiaries: 1200, budget: 120000, status: "active", description: "برنامج تعليم اللغة العربية للمسلمين غير الناطقين بها، يشمل مستويات مبتدئ ومتوسط ومتقدم" },
  { id: "pr2", name: "حلقات تحفيظ القرآن الكريم", type: "دروس أسبوعية", targetGroup: "الجميع", beneficiaries: 850, budget: 95000, status: "active", description: "حلقات منتظمة لتحفيظ القرآن الكريم بإشراف معلمين متخصصين" },
  { id: "pr3", name: "دورة فقه العبادات", type: "دورة مكثفة", targetGroup: "الهندية", beneficiaries: 320, budget: 45000, status: "active", description: "دورة مكثفة في فقه الصلاة والصيام والزكاة باللغة الأردية والهندية" },
  { id: "pr4", name: "برنامج إفطار الصائمين", type: "إفطار جماعي", targetGroup: "الجميع", beneficiaries: 2100, budget: 180000, status: "active", description: "تقديم وجبات إفطار يومية خلال شهر رمضان في 12 موقعاً" },
  { id: "pr5", name: "الرحلات الدعوية الميدانية", type: "رحلات دعوية", targetGroup: "الجميع", beneficiaries: 340, budget: 35000, status: "active", description: "زيارات ميدانية للمصانع والمزارع وسكن العمال لتوعيتهم دينياً" },
  { id: "pr6", name: "دورة العقيدة الإسلامية", type: "دورة مكثفة", targetGroup: "الباكستانية", beneficiaries: 280, budget: 28000, status: "planned", description: "دورة في أصول العقيدة الإسلامية باللغة الأردية" },
  { id: "pr7", name: "مسابقة حفظ القرآن السنوية", type: "مسابقات", targetGroup: "الجميع", beneficiaries: 180, budget: 50000, status: "planned", description: "مسابقة سنوية لحفظ القرآن الكريم بجوائز قيمة" },
];

const DEMO_BENEFICIARIES = [
  { id: "b1", name: "راجيش كومار", nationality: "هندية", gender: "ذكر", age: 34, program: "دروس عربية", mosque: "مسجد التوحيد", joinDate: "1446/01/05" },
  { id: "b2", name: "عمران خان", nationality: "باكستانية", gender: "ذكر", age: 28, program: "تلاوة قرآن", mosque: "مسجد السلام", joinDate: "1446/01/12" },
  { id: "b3", name: "عبدالرحيم بنغالي", nationality: "بنغالية", gender: "ذكر", age: 31, program: "فقه العبادات", mosque: "مسجد الفرقان", joinDate: "1446/02/01" },
  { id: "b4", name: "ماريا سانتوس", nationality: "فلبينية", gender: "أنثى", age: 26, program: "دروس عربية", mosque: "مسجد الإيمان", joinDate: "1446/02/15" },
  { id: "b5", name: "أحمد جاوا", nationality: "إندونيسية", gender: "ذكر", age: 38, program: "تلاوة قرآن", mosque: "مسجد الهدى", joinDate: "1446/03/01" },
  { id: "b6", name: "فاطمة بيبي", nationality: "باكستانية", gender: "أنثى", age: 29, program: "دروس عربية", mosque: "مسجد النور", joinDate: "1446/03/10" },
  { id: "b7", name: "سريكانت راو", nationality: "هندية", gender: "ذكر", age: 42, program: "فقه العبادات", mosque: "مسجد التوحيد", joinDate: "1446/01/20" },
  { id: "b8", name: "محمد ألامين", nationality: "أفريقية", gender: "ذكر", age: 25, program: "تلاوة قرآن", mosque: "مسجد الرحمة", joinDate: "1446/02/08" },
  { id: "b9", name: "بيرمال تامانغ", nationality: "نيبالية", gender: "ذكر", age: 33, program: "دروس عربية", mosque: "مسجد السلام", joinDate: "1446/03/15" },
  { id: "b10", name: "نور جهان", nationality: "بنغالية", gender: "أنثى", age: 27, program: "محادثة عربية", mosque: "مسجد الإيمان", joinDate: "1446/04/01" },
];

/* ═════════════════════════════════════════════
   ✦ Styles
   ═════════════════════════════════════════════ */
const styles = {
  kpiRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
    gap: 20,
    marginBottom: 16,
  },
  sectionTitle: {
    display: "flex", alignItems: "center", gap: 8,
    fontSize: 16, fontWeight: 700, color: C.primary, margin: "0 0 14px",
  },
  toolbar: {
    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap",
  },
  searchBox: {
    display: "flex", alignItems: "center", gap: 8, background: C.bg,
    borderRadius: 8, padding: "6px 12px", border: `1px solid ${C.border}`, flex: 1, maxWidth: 320,
  },
  searchInput: {
    border: "none", background: "transparent", outline: "none",
    fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, width: "100%",
  },
  filterSelect: {
    padding: "7px 12px", borderRadius: 8, border: `1px solid ${C.border}`,
    fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, background: C.white, cursor: "pointer",
  },
  detailRow: { fontSize: 14, color: C.primary, lineHeight: 1.6 },

  /* Nationalities */
  natGrid: { display: "flex", flexDirection: "column", gap: 10 },
  natCard: {
    display: "flex", alignItems: "center", gap: 12, padding: "8px 0",
    borderBottom: `1px solid ${C.bg}`,
  },
  natRank: {
    width: 28, height: 28, borderRadius: 8, display: "flex", alignItems: "center",
    justifyContent: "center", fontSize: 13, fontWeight: 700, color: C.primary, flexShrink: 0,
  },
  natBar: {
    width: 100, height: 8, borderRadius: 4, background: C.bg, overflow: "hidden",
  },

  /* Programs Summary */
  progSummaryGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12,
  },
  progSummaryCard: {
    display: "flex", alignItems: "center", gap: 12, padding: 12,
    background: C.bg, borderRadius: 10, border: `1px solid ${C.border}`,
  },
  progIcon: {
    width: 42, height: 42, borderRadius: 10, display: "flex",
    alignItems: "center", justifyContent: "center", flexShrink: 0,
  },

  /* Preachers */
  preachersGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))", gap: 16,
  },
  preacherCard: { padding: 18, cursor: "pointer" },
  preacherTop: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
  },
  preacherAvatar: {
    width: 46, height: 46, borderRadius: 12,
    background: `linear-gradient(135deg, ${C.primary}, ${C.teal})`,
    color: C.white, display: "flex", alignItems: "center", justifyContent: "center",
    fontWeight: 700, fontSize: 18,
  },
  langTag: {
    padding: "3px 8px", background: `${C.teal}12`, color: C.teal,
    borderRadius: 6, fontSize: 11, fontWeight: 600,
  },
  preacherStats: {
    display: "flex", gap: 20, marginTop: 10, paddingTop: 10,
    borderTop: `1px solid ${C.border}`, fontSize: 12, color: C.textLight,
  },
  preacherMosques: {
    display: "flex", alignItems: "center", gap: 4, marginTop: 8,
  },
};
